import { useParams, useNavigate } from "react-router-dom";
import { Star, Users, Clock, Award, BookOpen } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCourseDetail } from "@/hooks/useCourseDetail";
import { useAuth } from "@/hooks/useAuth";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ModuleAccordion } from "@/components/courses/ModuleAccordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { LoadingAnimation } from "@/components/LoadingAnimation";
import { toast } from "sonner";
import { useState } from "react";

const CourseDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { course, loading, error, enrollInCourse, submitReview } = useCourseDetail(slug || "");
  
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  const handleEnroll = async () => {
    if (!user) {
      toast.error(t("courseDetail.loginToEnroll"));
      navigate("/auth");
      return;
    }

    const result = await enrollInCourse();
    if (result.success) {
      toast.success(t("courseDetail.enrollmentSuccess"));
      // Navigate to course player after successful enrollment
      setTimeout(() => {
        navigate(`/courses/${slug}/learn`);
      }, 1000);
    } else {
      toast.error(t("courseDetail.enrollmentError"));
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error(t("courseDetail.loginToReview"));
      navigate("/auth");
      return;
    }

    if (!course?.is_enrolled) {
      toast.error("You must be enrolled to write a review");
      return;
    }

    setSubmittingReview(true);
    const result = await submitReview(reviewRating, reviewComment);
    if (result.success) {
      toast.success(t("courseDetail.reviewSuccess"));
      setReviewComment("");
      setReviewRating(5);
    } else {
      toast.error(t("courseDetail.reviewError"));
    }
    setSubmittingReview(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingAnimation size="lg" />
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">Course not found</h1>
          <Button onClick={() => navigate("/courses")}>Back to Courses</Button>
        </div>
      </div>
    );
  }

  const totalModules = course.modules?.length || 0;
  const totalLessons = course.modules?.reduce((sum, m) => sum + m.lessons.length, 0) || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left: Course Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <button onClick={() => navigate("/courses")} className="hover:text-primary">
                  {t("courses.title")}
                </button>
                <span>/</span>
                <span className="text-foreground">{course.title}</span>
              </div>

              {/* Title & Badges */}
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {course.is_featured && (
                    <Badge variant="default">{t("courses.featured")}</Badge>
                  )}
                  <Badge variant="secondary">{course.level}</Badge>
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 holographic-text">
                  {course.title}
                </h1>
                <p className="text-lg text-muted-foreground">
                  {course.description}
                </p>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                  <span className="font-semibold">{course.average_rating?.toFixed(1) || "0.0"}</span>
                  <span className="text-muted-foreground">
                    ({course.review_count} {t("courseDetail.reviews")})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <span>{course.enrollment_count} {t("courseDetail.studentsEnrolled")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <span>{course.duration_hours} {t("courses.hours")}</span>
                </div>
              </div>

              {/* Instructor */}
              <div className="flex items-center gap-4 glass-card p-4 rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={course.instructor?.avatar_url || ""} />
                  <AvatarFallback>{course.instructor?.full_name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm text-muted-foreground">{t("courseDetail.instructor")}</p>
                  <p className="font-semibold">{course.instructor?.full_name}</p>
                </div>
              </div>
            </div>

            {/* Right: Enrollment Card */}
            <div className="lg:col-span-1">
              <div className="glass-card p-6 rounded-lg sticky top-24 space-y-4">
                {/* Thumbnail */}
                <div className="aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-accent/20">
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-16 w-16 text-primary/40" />
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="text-center py-2">
                  {course.price && course.price > 0 ? (
                    <div>
                      <span className="text-3xl font-bold">{course.price}</span>
                      <span className="text-lg text-muted-foreground ml-1">
                        {course.currency}
                      </span>
                    </div>
                  ) : (
                    <span className="text-3xl font-bold text-primary">
                      {t("courses.free")}
                    </span>
                  )}
                </div>

                {/* Enroll Button */}
                {course.is_enrolled ? (
                  <Button 
                    className="w-full" 
                    size="lg" 
                    variant="default"
                    onClick={() => navigate(`/courses/${slug}/learn`)}
                  >
                    {t("courseDetail.startLearning")}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleEnroll}
                  >
                    {t("courseDetail.enrollNow")}
                  </Button>
                )}

                {/* Course Includes */}
                <div className="space-y-2 pt-4 border-t border-border/40">
                  <div className="flex items-center gap-3 text-sm">
                    <BookOpen className="h-4 w-4 text-primary" />
                    <span>{totalModules} {t("courseDetail.modules")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 text-primary" />
                    <span>{course.duration_hours} {t("courses.hours")}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Award className="h-4 w-4 text-primary" />
                    <span>{t("courseDetail.certificate")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="glass-card border-border/40 mb-8">
              <TabsTrigger value="overview">{t("courseDetail.overview")}</TabsTrigger>
              <TabsTrigger value="curriculum">{t("courseDetail.curriculum")}</TabsTrigger>
              <TabsTrigger value="reviews">{t("courseDetail.reviews")}</TabsTrigger>
              <TabsTrigger value="instructor">{t("courseDetail.instructor")}</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              <div className="glass-card p-8 rounded-lg">
                <h2 className="text-2xl font-bold mb-4">{t("courseDetail.description")}</h2>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {course.description}
                </p>
              </div>
            </TabsContent>

            {/* Curriculum Tab */}
            <TabsContent value="curriculum">
              <ModuleAccordion
                modules={course.modules || []}
                isEnrolled={course.is_enrolled || false}
              />
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews" className="space-y-6">
              {/* Write Review */}
              {course.is_enrolled && (
                <div className="glass-card p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-4">{t("courseDetail.writeReview")}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t("courseDetail.yourRating")}
                      </label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setReviewRating(star)}
                            className="transition-transform hover:scale-110"
                          >
                            <Star
                              className={`h-6 w-6 ${
                                star <= reviewRating
                                  ? "text-yellow-500 fill-yellow-500"
                                  : "text-muted-foreground"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t("courseDetail.yourReview")}
                      </label>
                      <Textarea
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Share your experience..."
                        rows={4}
                      />
                    </div>
                    <Button onClick={handleSubmitReview} disabled={submittingReview}>
                      {t("courseDetail.submit")}
                    </Button>
                  </div>
                </div>
              )}

              {/* Reviews List */}
              <div className="space-y-4">
                {course.reviews && course.reviews.length > 0 ? (
                  course.reviews.map((review) => (
                    <div key={review.id} className="glass-card p-6 rounded-lg">
                      <div className="flex items-start gap-4">
                        <Avatar>
                          <AvatarImage src={review.user?.avatar_url || ""} />
                          <AvatarFallback>{review.user?.full_name?.[0] || "U"}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold">{review.user?.full_name || "Anonymous"}</p>
                            <div className="flex gap-1">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating
                                      ? "text-yellow-500 fill-yellow-500"
                                      : "text-muted-foreground"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                          {review.comment && (
                            <p className="mt-3 text-foreground">{review.comment}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="glass-card p-8 rounded-lg text-center">
                    <p className="text-muted-foreground">{t("courseDetail.noReviews")}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Instructor Tab */}
            <TabsContent value="instructor" className="space-y-6">
              <div className="glass-card p-8 rounded-lg">
                <div className="flex items-start gap-6 mb-6">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={course.instructor?.avatar_url || ""} />
                    <AvatarFallback className="text-2xl">
                      {course.instructor?.full_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-2xl font-bold mb-2">{course.instructor?.full_name}</h2>
                    <p className="text-muted-foreground">{t("courseDetail.instructor")}</p>
                  </div>
                </div>
                {course.instructor?.bio && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">
                      {t("courseDetail.instructorBio")}
                    </h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {course.instructor.bio}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CourseDetail;
